import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ExternalLink, Eye } from "lucide-react";
import {
  Pill, EmptyState, Loading, ErrorState, TableShell, Th, Card, KV,
  Drawer, ConfirmModal, ExportButton,
} from "./AdminUI";

const STATUS_TONE = { draft: "neutral", scheduled: "amber", sent: "green", canceled: "red" };

export default function LegalTab() {
  const [docs, setDocs] = useState(null);
  const [notices, setNotices] = useState(null);
  const [error, setError] = useState(null);
  const [composing, setComposing] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    setError(null);
    api.get("/admin/legal/documents").then(r => setDocs(r.data.documents))
      .catch(e => setError(e.response?.data?.detail || e.message));
    api.get("/admin/legal/notices").then(r => setNotices(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!docs || !notices) return <Loading />;

  return (
    <div className="space-y-5" data-testid="admin-legal-tab">
      <div className="bg-white rounded-2xl px-4 py-3 border border-black/10" data-testid="legal-mock-banner">
        <p className="text-xs text-[#222]/65">
          Legal notices are <b>account communications</b>, not marketing — they reach every targeted account
          regardless of email preferences. While Resend is unconfigured, email delivery is <b>MOCKED</b> to
          logs; in-app notices still appear normally. Sent notices are permanent and cannot be edited.
        </p>
      </div>

      <section>
        <h3 className="text-sm font-bold mb-3" style={{ fontFamily: "Outfit" }}>Policy documents</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {docs.map(doc => (
            <Card key={doc.key} testid={`legal-doc-${doc.key}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <p className="font-bold text-sm truncate" style={{ fontFamily: "Outfit" }}>{doc.title}</p>
                  <p className="text-xs text-[#222]/50">Effective {doc.effective_date}</p>
                </div>
                <Pill tone="green">v{doc.version}</Pill>
              </div>
              <KV label="Sections" value={doc.section_count} />
              <KV label="Acceptance required for" value={doc.required_for.length ? doc.required_for.join(", ") : "informational"} />
              <KV label="Accepted current version" value={doc.accepted_current_version} />
              <KV label="Accepted any version" value={doc.accepted_any_version} />
              <KV label="Prior versions on record" value={doc.prior_versions.length ? doc.prior_versions.join(", ") : "none"} />
              <KV label="Notices sent" value={doc.notices_sent} />
              <div className="flex items-center gap-3 mt-3">
                <a href={doc.public_path} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#245544]"
                  data-testid={`legal-view-${doc.key}`}>
                  View public page <ExternalLink size={11} />
                </a>
                <button onClick={() => setComposing({ policy_key: doc.key, prior_version: doc.version })}
                  className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-black/5"
                  data-testid={`legal-notice-new-${doc.key}`}>
                  <Plus size={12} /> New notice
                </button>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h3 className="text-sm font-bold" style={{ fontFamily: "Outfit" }}>Notification history</h3>
          <div className="flex items-center gap-2">
            {Object.entries(notices.counts).map(([k, v]) => v > 0 && (
              <Pill key={k} tone={STATUS_TONE[k]}>{k}: {v}</Pill>
            ))}
            <ExportButton kind="legal_notices" label="Legal notice history" />
          </div>
        </div>
        {notices.notices.length === 0 ? (
          <EmptyState title="No legal notices yet"
            body="Create one from any policy card above when you publish a new version."
            testid="legal-notices-empty" />
        ) : (
          <TableShell testid="legal-notices-table" maxHeight="50vh">
            <thead>
              <tr>
                <Th>Policy</Th><Th>Version</Th><Th>Audience</Th><Th>Status</Th>
                <Th className="hidden lg:table-cell">Reach</Th><Th className="text-right">Open</Th>
              </tr>
            </thead>
            <tbody>
              {notices.notices.map(n => (
                <tr key={n.id} className="border-t border-black/5" data-testid={`legal-notice-row-${n.id}`}>
                  <td className="px-4 py-3 text-xs font-medium max-w-[200px] truncate">{n.policy_title}</td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">
                    {n.prior_version ? `${n.prior_version} → ` : ""}<b>{n.new_version}</b>
                  </td>
                  <td className="px-4 py-3 text-xs">{n.audience}</td>
                  <td className="px-4 py-3"><Pill tone={STATUS_TONE[n.status]}>{n.status}</Pill></td>
                  <td className="px-4 py-3 text-xs hidden lg:table-cell whitespace-nowrap">
                    {n.status === "sent"
                      ? `${n.recipients_total} accounts · ${n.in_app_created} in-app · ${n.email_attempted} email`
                      : n.scheduled_for ? new Date(n.scheduled_for).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setOpenId(n.id)}
                      className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold"
                      data-testid={`legal-notice-open-${n.id}`}><Eye size={12} /> Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </section>

      {composing && <Composer seed={composing} docs={docs} onClose={() => setComposing(null)} onSaved={load} />}
      {openId && <NoticeDrawer id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

const AUDIENCES = [["all", "All accounts"], ["clients", "Clients only"],
  ["pros", "Professionals only"], ["pro_plus", "Pro Plus only"]];

function Composer({ seed, docs, onClose, onSaved }) {
  const [form, setForm] = useState({
    policy_key: seed.policy_key,
    prior_version: seed.prior_version || "",
    new_version: "",
    publication_date: new Date().toISOString().slice(0, 10),
    effective_date: "",
    change_summary: "",
    audience: "all",
    send_email: true,
    show_in_app: true,
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const doc = docs.find(d => d.key === form.policy_key);
  const valid = form.new_version.trim() && form.effective_date.trim() && form.change_summary.trim().length >= 10;

  const runPreview = async () => {
    try { const r = await api.post("/admin/legal/notices/preview", form); setPreview(r.data); }
    catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Preview failed"); }
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.post("/admin/legal/notices", form);
      toast.success("Draft saved — nothing has been sent");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  return (
    <Drawer title="New legal notice" subtitle={doc?.title} onClose={onClose} testid="legal-composer">
      <Card title="What changed">
        <label className="block text-xs font-semibold mb-1 mt-2">Policy</label>
        <select value={form.policy_key} onChange={e => set("policy_key", e.target.value)}
          className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none"
          data-testid="composer-policy">
          {docs.map(d => <option key={d.key} value={d.key}>{d.title}</option>)}
        </select>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-xs font-semibold mb-1">Prior version</label>
            <input value={form.prior_version} onChange={e => set("prior_version", e.target.value)}
              placeholder="2026-06-20" className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none"
              data-testid="composer-prior-version" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">New version *</label>
            <input value={form.new_version} onChange={e => set("new_version", e.target.value)}
              placeholder="2026-09-11" className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none"
              data-testid="composer-new-version" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">Publication date</label>
            <input type="date" value={form.publication_date} onChange={e => set("publication_date", e.target.value)}
              className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none"
              data-testid="composer-publication-date" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">Effective date *</label>
            <input value={form.effective_date} onChange={e => set("effective_date", e.target.value)}
              placeholder="September 11, 2026"
              className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none"
              data-testid="composer-effective-date" />
          </div>
        </div>

        <label className="block text-xs font-semibold mb-1 mt-3">Change summary * (shown in the email and in-app notice)</label>
        <textarea rows={4} value={form.change_summary} onChange={e => set("change_summary", e.target.value)}
          placeholder="Plain-language description of what changed and why it matters."
          className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none"
          data-testid="composer-summary" />
      </Card>

      <Card title="Who gets it">
        <select value={form.audience} onChange={e => set("audience", e.target.value)}
          className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none"
          data-testid="composer-audience">
          {AUDIENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <div className="flex flex-col gap-2 mt-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.send_email} onChange={e => set("send_email", e.target.checked)}
              data-testid="composer-send-email" /> Send transactional email
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.show_in_app} onChange={e => set("show_in_app", e.target.checked)}
              data-testid="composer-show-in-app" /> Show a dismissible in-app notice
          </label>
        </div>
        <p className="text-[11px] text-[#222]/45 mt-3">
          Marketing and digest opt-outs are deliberately bypassed — this is a legal/account notice.
        </p>
      </Card>

      <div className="flex flex-wrap gap-2">
        <button onClick={runPreview} disabled={!valid}
          className="text-xs px-4 py-2 rounded-full bg-black/5 font-semibold disabled:opacity-50"
          data-testid="composer-preview">Preview</button>
        <button onClick={saveDraft} disabled={!valid || busy}
          className="text-xs px-4 py-2 rounded-full bg-[#2E6B57] text-white font-semibold disabled:opacity-50"
          data-testid="composer-save-draft">{busy ? "Saving…" : "Save draft"}</button>
      </div>
      {!valid && <p className="text-[11px] text-[#222]/45">New version, effective date and a summary of at least 10 characters are required.</p>}

      {preview && (
        <>
          <Card title="Email preview" testid="composer-email-preview">
            <p className="text-xs text-[#222]/45 mb-1">Subject</p>
            <p className="text-sm font-semibold mb-3">{preview.email_preview.subject}</p>
            <div className="bg-white border border-black/10 rounded-xl p-4">
              <p className="font-bold text-base mb-2" style={{ fontFamily: "Outfit" }}>{preview.email_preview.heading}</p>
              {preview.email_preview.body_lines.filter(Boolean).map((l, i) => (
                <p key={i} className="text-sm text-[#222]/75 mb-2">{l}</p>
              ))}
              <p className="text-[11px] text-[#222]/45">{preview.email_preview.version_line}</p>
              <span className="inline-block mt-3 px-4 py-2 rounded-full bg-[#2E6B57] text-white text-xs font-semibold">
                {preview.email_preview.cta_label}
              </span>
            </div>
          </Card>
          <Card title="In-app notice preview" testid="composer-inapp-preview">
            <div className="bg-[#2E6B57]/10 border border-[#2E6B57]/25 rounded-xl p-3">
              <p className="text-sm font-semibold">{preview.in_app_preview.title}</p>
              <p className="text-xs text-[#222]/70 mt-1">{preview.in_app_preview.body}</p>
            </div>
            <p className="text-xs text-[#222]/55 mt-3">
              Will reach <b>{preview.audience_size}</b> account{preview.audience_size === 1 ? "" : "s"}.
            </p>
          </Card>
        </>
      )}
    </Drawer>
  );
}

function NoticeDrawer({ id, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [modal, setModal] = useState(null);
  const [when, setWhen] = useState("");

  const load = useCallback(() => api.get(`/admin/legal/notices/${id}`).then(r => setD(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <Drawer title="Legal notice" onClose={onClose}><Loading /></Drawer>;
  const n = d.notice;

  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); await load(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Drawer title={n.policy_title} subtitle={`Version ${n.new_version} · ${n.status}`} onClose={onClose}
      testid="legal-notice-drawer">
      <Card title="Notice">
        <KV label="Status" value={n.status} />
        <KV label="Prior → new version" value={`${n.prior_version || "—"} → ${n.new_version}`} />
        <KV label="Publication date" value={n.publication_date} />
        <KV label="Effective date" value={n.effective_date} />
        <KV label="Audience" value={`${n.audience} (${d.audience_size} accounts today)`} />
        <KV label="Email" value={n.send_email ? "Yes" : "No"} />
        <KV label="In-app notice" value={n.show_in_app ? "Yes (dismissible)" : "No"} />
        <KV label="Created by" value={`${n.created_by_email} · ${new Date(n.created_at).toLocaleString()}`} />
        {n.status === "sent" && <>
          <KV label="Sent at" value={new Date(n.sent_at).toLocaleString()} />
          <KV label="Recipients" value={n.recipients_total} />
          <KV label="In-app notices created" value={n.in_app_created} />
          <KV label="Emails attempted" value={n.email_attempted} />
          <KV label="Emails accepted by provider" value={`${n.email_delivered} (0 expected while mocked)`} />
          <KV label="Dismissals" value={d.dismissals} />
        </>}
        <p className="text-sm mt-3 whitespace-pre-line">{n.change_summary}</p>
      </Card>

      <Card title="Email preview">
        <p className="text-sm font-semibold mb-2">{d.email_preview.subject}</p>
        {d.email_preview.body_lines.filter(Boolean).map((l, i) => (
          <p key={i} className="text-xs text-[#222]/70 mb-1.5">{l}</p>
        ))}
      </Card>

      {n.status !== "sent" && n.status !== "canceled" && (
        <Card title="Publish">
          <div className="flex flex-wrap items-center gap-2">
            <input type="datetime-local" value={when} onChange={e => setWhen(e.target.value)}
              className="px-3 py-2 border border-black/10 rounded-xl text-sm outline-none"
              data-testid="notice-schedule-input" />
            <button disabled={!when}
              onClick={() => run(() => api.post(`/admin/legal/notices/${id}/schedule`,
                { scheduled_for: new Date(when).toISOString() }), "Scheduled")}
              className="text-xs px-4 py-2 rounded-full bg-black/5 font-semibold disabled:opacity-50"
              data-testid="notice-schedule">Schedule</button>
            <button onClick={() => setModal("send")}
              className="text-xs px-4 py-2 rounded-full bg-[#2E6B57] text-white font-semibold"
              data-testid="notice-send">Send now</button>
            {n.status === "scheduled" && (
              <button onClick={() => run(() => api.post(`/admin/legal/notices/${id}/cancel`), "Cancelled")}
                className="text-xs px-4 py-2 rounded-full bg-red-100 text-red-700 font-semibold"
                data-testid="notice-cancel">Cancel schedule</button>
            )}
          </div>
        </Card>
      )}

      {modal === "send" && (
        <ConfirmModal title="Send this legal notice now" danger confirmLabel="Send legal notice"
          impact={`This will immediately notify ${d.audience_size} account(s) in the "${n.audience}" audience.\n`
            + `${n.show_in_app ? "• A dismissible in-app notice is created for each account.\n" : ""}`
            + `${n.send_email ? "• A transactional email is attempted for each account — email delivery is currently MOCKED to logs, so nothing leaves the server.\n" : ""}`
            + "• Marketing and digest opt-outs are bypassed because this is a legal/account notice.\n"
            + "• Once sent, the notice becomes permanent history and cannot be edited or unsent.\n"
            + "• No acceptance record is created or altered."}
          onConfirm={() => run(() => api.post(`/admin/legal/notices/${id}/send`), "Legal notice sent")}
          onClose={() => setModal(null)} />
      )}
    </Drawer>
  );
}
